// What a visitor sees of analytics: the one-line consent bar, and the
// ANALYTICS row the menu's Settings tab carries so the choice can be changed later.

export const PRIVACY_HREF = 'privacy.html';

const BAR_TEXT = '4XCiv counts visits and which parts of the solar system people explore. No accounts, no names.';

const ROW_TIP = 'Counts the views you open and bodies you visit, with no account or name. OFF sends nothing. '
  + 'The privacy page says what is kept.';
const ROW_TIP_SIGNAL = 'Your browser sends Do Not Track or Global Privacy Control, so nothing is counted.';

export interface ConsentBarHandle {
  readonly shown: boolean;
  show(): void;
  hide(): void;
}

export function createConsentBar(on: { allow(): void; deny(): void }): ConsentBarHandle {
  let bar: HTMLElement | null = null;
  function button(id: string, text: string, fn: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.id = id;
    b.type = 'button';
    b.className = 'panel-btn';
    b.style.cssText = 'padding:6px 12px;letter-spacing:0.15em;';
    b.textContent = text;
    b.addEventListener('click', fn);
    return b;
  }
  return {
    get shown() { return bar !== null; },
    show(): void {
      if (bar !== null) return;
      const el = document.createElement('div');
      el.id = 'analytics-consent';
      el.className = 'panel';
      el.setAttribute('role', 'region');
      el.setAttribute('aria-label', 'Analytics consent');
      el.style.cssText = 'position:fixed;left:50%;bottom:12px;transform:translateX(-50%);z-index:950;'
        + 'display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:8px 12px;'
        + 'max-width:calc(100vw - 32px);box-sizing:border-box;font-size:11px;user-select:text;';
      const text = document.createElement('span');
      text.textContent = BAR_TEXT;
      const link = document.createElement('a');
      link.href = PRIVACY_HREF;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = 'Privacy';
      link.style.color = 'inherit';
      el.append(text, button('analytics-allow', 'ALLOW', () => on.allow()),
        button('analytics-deny', 'NO THANKS', () => on.deny()), link);
      document.body.appendChild(el);
      bar = el;
    },
    hide(): void {
      bar?.remove();
      bar = null;
    },
  };
}

export interface ControlsRowHandle {
  paint(state: { on: boolean; signal: boolean }): void;
  dispose(): void;
}

let privacyRow: HTMLElement | null = null;

/** The ANALYTICS row, built once and drawn by the menu's Settings tab each time it opens; null on a page with no analytics. */
export function privacySection(): HTMLElement | null {
  return privacyRow;
}

export function createControlsRow(onToggle: () => void): ControlsRowHandle {
  const section = document.createElement('section');
  section.id = 'analytics-controls';
  const dl = document.createElement('dl');
  const dt = document.createElement('dt');
  dt.className = 'hud-tip';
  dt.textContent = 'ANALYTICS';
  const dd = document.createElement('dd');
  const toggle = document.createElement('button');
  toggle.id = 'analytics-toggle';
  toggle.type = 'button';
  toggle.className = 'panel-btn';
  toggle.style.cssText = 'padding:2px 10px;letter-spacing:0.15em;';
  toggle.addEventListener('click', onToggle);
  const state = document.createElement('span');
  state.id = 'analytics-state';
  const link = document.createElement('a');
  link.href = PRIVACY_HREF;
  link.target = '_blank';
  link.rel = 'noopener';
  link.textContent = 'privacy';
  link.style.cssText = 'color:inherit;margin-left:8px;';
  dd.append(toggle, state, link);
  dl.append(dt, dd);
  section.append(dl);
  privacyRow = section;
  return {
    paint({ on, signal }): void {
      dt.dataset['tooltip'] = signal ? ROW_TIP_SIGNAL : ROW_TIP;
      // A browser privacy signal holds it off, so there is nothing for a button to do.
      toggle.hidden = signal;
      state.textContent = signal ? 'OFF' : '';
      toggle.textContent = on ? 'ON' : 'OFF';
      toggle.title = on ? 'Counted: press to stop' : 'Not counted: press to allow';
    },
    dispose(): void {
      toggle.removeEventListener('click', onToggle);
      section.remove();
      if (privacyRow === section) privacyRow = null;
    },
  };
}
